import { type CellError, type Expr, type Value, cellKey, parse } from "@package/scratchpad-utils";
import type { Graph, Node } from "../types";
import { type EvalContext, FormulaError, type FunctionRegistry, evaluate } from "./interpreter";

export type RefKey = (ownerId: string, sheet: string | undefined, col: number, row: number) => string | null;

const defaultRefKey: RefKey = (_owner, _sheet, col, row) => cellKey(col, row);

function parseLiteral(src: string): Value | null {
  if (src === "") return null;
  const n = Number(src);
  return Number.isNaN(n) ? src : n;
}

function toCellError(e: unknown): CellError {
  if (e instanceof FormulaError) return { code: e.code };
  throw e;
}

function extractDeps(ast: Expr, ownerId: string, refKey: RefKey, out: Set<string>): void {
  switch (ast.kind) {
    case "ref": {
      const { start, end } = ast.addr;
      if (!end) {
        const k = refKey(ownerId, start.sheet, start.col, start.row);
        if (k !== null) out.add(k);
        return;
      }
      for (let col = Math.min(start.col, end.col); col <= Math.max(start.col, end.col); col++) {
        for (let row = Math.min(start.row, end.row); row <= Math.max(start.row, end.row); row++) {
          const k = refKey(ownerId, start.sheet, col, row);
          if (k !== null) out.add(k);
        }
      }
      return;
    }
    case "unary":
      extractDeps(ast.operand, ownerId, refKey, out);
      return;
    case "binary":
      extractDeps(ast.left, ownerId, refKey, out);
      extractDeps(ast.right, ownerId, refKey, out);
      return;
    case "call":
      for (const arg of ast.args) extractDeps(arg, ownerId, refKey, out);
      return;
    default:
      return;
  }
}

export function createGraph(
  functions: FunctionRegistry,
  refKey: RefKey = defaultRefKey,
  resolveExternal?: (key: string) => Value | null | undefined,
): Graph {
  const nodes = new Map<string, Node>();
  const dependents = new Map<string, Set<string>>();
  const dirty = new Set<string>();
  const subscribers = new Set<(node: Node) => void>();
  const externalsByNode = new Map<string, Set<string>>();
  const nodesByExternal = new Map<string, Set<string>>();
  const activeExternals = new Set<string>();
  const externalSubscribers = new Set<(key: string, active: boolean) => void>();

  const syncExternals = () => {
    for (const [key, ids] of nodesByExternal) {
      if (ids.size > 0 && !activeExternals.has(key)) {
        activeExternals.add(key);
        for (const cb of externalSubscribers) cb(key, true);
      }
    }
    for (const key of [...activeExternals]) {
      const ids = nodesByExternal.get(key);
      if (!ids || ids.size === 0) {
        activeExternals.delete(key);
        nodesByExternal.delete(key);
        for (const cb of externalSubscribers) cb(key, false);
      }
    }
  };

  const clearExternals = (id: string) => {
    for (const key of externalsByNode.get(id) ?? []) nodesByExternal.get(key)?.delete(id);
    externalsByNode.delete(id);
  };

  const trackExternal = (id: string, key: string) => {
    let keys = externalsByNode.get(id);
    if (!keys) {
      keys = new Set();
      externalsByNode.set(id, keys);
    }
    keys.add(key);
    let ids = nodesByExternal.get(key);
    if (!ids) {
      ids = new Set();
      nodesByExternal.set(key, ids);
    }
    ids.add(id);
  };

  const notify = (node: Node) => {
    for (const cb of subscribers) cb(node);
  };

  const markDirty = (id: string) => {
    if (dirty.has(id)) return;
    dirty.add(id);
    for (const dependent of dependents.get(id) ?? []) markDirty(dependent);
  };

  const set = (id: string, src: string) => {
    const prev = nodes.get(id);
    for (const dep of prev?.deps ?? []) dependents.get(dep)?.delete(id);
    clearExternals(id);

    let ast: Expr | null = null;
    let literal: Value | null = null;
    let error: CellError | null = null;
    const deps = new Set<string>();
    if (src.startsWith("=")) {
      try {
        ast = parse(src.slice(1));
        extractDeps(ast, id, refKey, deps);
      } catch {
        ast = null;
        error = { code: "#ERROR!" };
      }
    } else {
      literal = parseLiteral(src);
    }
    for (const dep of deps) {
      let depSet = dependents.get(dep);
      if (!depSet) {
        depSet = new Set();
        dependents.set(dep, depSet);
      }
      depSet.add(id);
    }
    nodes.set(id, { id, ast, deps, value: literal, error, dirty: true });
    markDirty(id);
  };

  const remove = (id: string) => {
    const node = nodes.get(id);
    if (!node) return;
    for (const dep of node.deps) dependents.get(dep)?.delete(id);
    clearExternals(id);
    nodes.delete(id);
    dirty.delete(id);
    for (const dependent of dependents.get(id) ?? []) markDirty(dependent);
  };

  const dirtyExternal = (key: string) => {
    for (const id of nodesByExternal.get(key) ?? []) markDirty(id);
  };

  const topoSort = (ids: Set<string>): string[] => {
    const indeg = new Map<string, number>();
    for (const id of ids) indeg.set(id, 0);
    for (const id of ids) {
      for (const dep of nodes.get(id)?.deps ?? []) if (ids.has(dep)) indeg.set(id, (indeg.get(id) ?? 0) + 1);
    }
    const queue = [...ids].filter((id) => indeg.get(id) === 0);
    const order: string[] = [];
    while (queue.length) {
      const id = queue.shift()!;
      order.push(id);
      for (const dependent of dependents.get(id) ?? []) {
        if (!ids.has(dependent)) continue;
        const d = (indeg.get(dependent) ?? 0) - 1;
        indeg.set(dependent, d);
        if (d === 0) queue.push(dependent);
      }
    }
    return order;
  };

  const recompute = () => {
    const order = topoSort(dirty);
    const evaluated = new Set(order);
    for (const id of order) {
      const node = nodes.get(id);
      if (!node) continue;
      if (node.ast) {
        clearExternals(id);
        const ctx: EvalContext = {
          resolveCell: (sheet, col, row) => {
            const k = refKey(id, sheet, col, row);
            if (k === null) throw new FormulaError("#REF!");
            const target = nodes.get(k);
            if (target?.error) throw new FormulaError(target.error.code);
            return target?.value ?? null;
          },
          resolveExternal,
          trackExternal: (key) => trackExternal(id, key),
          functions,
        };
        try {
          node.value = evaluate(node.ast, ctx);
          node.error = null;
        } catch (e) {
          node.error = toCellError(e);
          node.value = null;
        }
      }
      node.dirty = false;
      notify(node);
    }
    for (const id of dirty) {
      if (evaluated.has(id)) continue;
      const node = nodes.get(id);
      if (!node) continue;
      node.error = { code: "#CIRCULAR!" };
      node.value = null;
      node.dirty = false;
      notify(node);
    }
    dirty.clear();
    syncExternals();
  };

  return {
    set,
    get: (id) => nodes.get(id),
    remove,
    dirtyExternal,
    recompute,
    subscribe: (cb) => {
      subscribers.add(cb);
      return () => subscribers.delete(cb);
    },
    subscribeExternals: (cb) => {
      externalSubscribers.add(cb);
      for (const key of activeExternals) cb(key, true);
      return () => externalSubscribers.delete(cb);
    },
  };
}
